import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import type { GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import CustomDataGrid from 'components/common/CustomDataGrid';
import type { FilterOption } from 'components/common/MasterFilter/types';
import { useBusinessUnits } from 'contexts/BusinessUnitsContext';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetLeadsQuery } from 'services/api/leads.api';
import type { ExtendedLead } from 'services/types/lead';
import { getLeadTableColumns } from 'utils/leadTableColumns';

import DownloadLeadsDialog from './DownloadLeadsDialog';

interface LeadsProps {
  businessUnitId: number | null;
  businessUnitType?: 'car_dealership' | 'financial_institution' | 'insurance_broker' | 'after_sales' | null;
  searchTerm?: string;
  externalFilterItems?: GridFilterModel['items'];
  onTotalChange?: (total: number) => void;
  onShowDiscardedChange?: (show: boolean) => void;
  refreshNonce?: number;
  sourceOptions?: FilterOption[];
  brandOptions?: FilterOption[];
  campaignOptions?: FilterOption[];
}

const Leads: FC<LeadsProps> = ({
  businessUnitId,
  businessUnitType = null,
  searchTerm = '',
  externalFilterItems,
  onTotalChange,
  onShowDiscardedChange,
  refreshNonce,
  sourceOptions,
  brandOptions,
  campaignOptions,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(window.location.search);
  const { getBusinessUnitById } = useBusinessUnits();
  const topicsConfig = businessUnitId ? getBusinessUnitById(businessUnitId)?.conversationTaggingConfig?.topics : null;

  const queryFilterModel = queryParams.get('filterModel');
  const queryPaginationModel = queryParams.get('paginationModel');
  const querySortModel = queryParams.get('sortModel');
  const queryHighlightedClientId = queryParams.get('highlightedClientId');
  const parsedQueryPaginationModel = queryPaginationModel
    ? (JSON.parse(queryPaginationModel) as GridPaginationModel)
    : null;
  const [openDownloadDialog, setOpenDownloadDialog] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
    parsedQueryPaginationModel && parsedQueryPaginationModel.pageSize <= 100 && parsedQueryPaginationModel.page >= 0
      ? parsedQueryPaginationModel
      : {
          page: 0,
          pageSize: 50,
        }
  );
  const [filterModel, setFilterModel] = useState<GridFilterModel>(() => {
    const parsed = queryFilterModel
      ? (JSON.parse(queryFilterModel) as GridFilterModel)
      : ({ items: [] } as GridFilterModel);
    return parsed;
  });
  const [sortModel, setSortModel] = useState<GridSortModel>(
    querySortModel ? (JSON.parse(querySortModel) as GridSortModel) : []
  );

  // LeadsTable seeds its `filterModel` from the URL on mount, and the parent also passes
  // `externalFilterItems` derived from the same URL (advanced filter + bar). That overlap
  // causes the same item (e.g. `whatsAppCampaign in X`) to appear twice in the request,
  // which Sequelize translates into two predicates against the LEFT-joined association and
  // returns zero rows. Deduplicate on (field, operator, value) before sending.
  const mergedFilterItems = useMemo(() => {
    const seen = new Set<string>();
    const out: GridFilterModel['items'] = [];
    for (const item of [...filterModel.items, ...(externalFilterItems ?? [])]) {
      const key = `${item.field}${item.operator}${JSON.stringify(item.value)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(item);
    }
    return out;
  }, [filterModel.items, externalFilterItems]);

  const { currentData, refetch, isFetching } = useGetLeadsQuery(
    {
      paginationModel: JSON.stringify(paginationModel),
      filterModel: JSON.stringify({ items: mergedFilterItems }),
      sortModel: JSON.stringify(sortModel),
      businessUnitId: businessUnitId || -1,
      ...(searchTerm ? { searchTerm } : {}),
    },
    // Skip until the business unit is known. Firing with a placeholder BU returns empty
    // rows and locks in an empty cache entry; the parent now syncs the BU from the URL on
    // mount so the first request waits for that sync before going out.
    { skip: businessUnitId == null }
  );

  const { rows: leads, count: total } = currentData || { rows: [], count: 0 };

  useEffect(() => {
    onTotalChange?.(total);
  }, [total, onTotalChange]);

  const sourceValues = useMemo(() => (sourceOptions ?? []).map((o) => o.value), [sourceOptions]);
  const initialBrandOfInterestValues = useMemo(() => (brandOptions ?? []).map((o) => o.value), [brandOptions]);
  const campaignValues = useMemo(() => (campaignOptions ?? []).map((o) => o.value), [campaignOptions]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Need those params to refetch
  useEffect(() => {
    refetch().catch(() => {
      console.error('Error fetching leads');
    });
  }, [paginationModel, businessUnitId, filterModel, sortModel, searchTerm]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: refetch only when parent bumps the nonce
  useEffect(() => {
    if (refreshNonce === undefined || refreshNonce === 0) return;
    refetch().catch(() => {
      console.error('Error fetching leads');
    });
  }, [refreshNonce]);

  useEffect(() => {
    const freshParams = new URLSearchParams(window.location.search);
    freshParams.set('paginationModel', JSON.stringify(paginationModel));
    navigate(`?${freshParams.toString()}`, { replace: true });
  }, [paginationModel, navigate]);

  useEffect(() => {
    const freshParams = new URLSearchParams(window.location.search);
    if (filterModel.items.length > 0) {
      freshParams.set('filterModel', JSON.stringify(filterModel));
    } else {
      freshParams.delete('filterModel');
    }
    navigate(`?${freshParams.toString()}`, { replace: true });
  }, [filterModel, navigate]);

  useEffect(() => {
    const freshParams = new URLSearchParams(window.location.search);
    if (sortModel.length > 0) {
      freshParams.set('sortModel', JSON.stringify(sortModel));
    } else {
      freshParams.delete('sortModel');
    }
    navigate(`?${freshParams.toString()}`, { replace: true });
  }, [sortModel, navigate]);

  const columns = getLeadTableColumns(
    sourceValues,
    initialBrandOfInterestValues,
    campaignValues,
    businessUnitType,
    businessUnitId,
    topicsConfig
  );

  const isDiscardedFalseItem = (i: GridFilterModel['items'][number]) =>
    i.field === 'discarded' &&
    (i as unknown as { operator?: string; value?: unknown }).operator === 'equals' &&
    (i as unknown as { value?: unknown }).value === false;

  const hasDiscardedFalseFilter =
    filterModel.items.some(isDiscardedFalseItem) || (externalFilterItems ?? []).some(isDiscardedFalseItem);

  const toggleShowDiscarded = (show: boolean) => {
    setFilterModel({
      ...filterModel,
      items: filterModel.items.filter((i) => i.field !== 'discarded'),
    });
    onShowDiscardedChange?.(show);
  };

  const navigateToClientId = (id: number, newPage = false) => {
    queryParams.set('highlightedClientId', id.toString());
    navigate(`?${queryParams.toString()}`, { replace: true });
    if (newPage) {
      window.open(`/clients/${id}`, '_blank');
      return;
    }
    navigate(`/clients/${id}`, {
      state: {
        clientsList: leads.map((lead) => (lead as ExtendedLead).client),
        from: location.pathname + location.search,
      },
    });
  };

  return (
    <Box style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <CustomDataGrid<ExtendedLead>
          rows={leads}
          columns={columns}
          filterModel={searchTerm ? { ...filterModel, quickFilterValues: [searchTerm] } : filterModel}
          sortModel={sortModel}
          onFilterModelChange={setFilterModel}
          onSortModelChange={setSortModel}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={total}
          isLoading={isFetching}
          disableFilters
          onRowClick={(params, event) =>
            navigateToClientId(Number(params.client?.id), event.ctrlKey || event.metaKey || event.shiftKey)
          }
          getRowClassName={(params) => {
            const classNames = [] as string[];
            if (params.client?.id === Number(queryHighlightedClientId)) {
              classNames.push('previous');
            }
            if ((params as unknown as ExtendedLead).discarded) {
              classNames.push('discarded');
            }
            return classNames.join(' ');
          }}
          pageId="leads"
          topBar={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '100%',
                overflow: 'scroll',
                gap: 1,
              }}
            >
              <Button sx={{ textTransform: 'none' }} onClick={() => setOpenDownloadDialog(true)}>
                Descargar leads
              </Button>
              <Box sx={{ ml: 2, display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={!hasDiscardedFalseFilter}
                      onChange={(e) => toggleShowDiscarded(e.target.checked)}
                      color={!hasDiscardedFalseFilter ? 'error' : 'primary'}
                    />
                  }
                  sx={{ m: 0 }}
                  label={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.primary',
                        }}
                      >
                        {!hasDiscardedFalseFilter ? 'Ocultar descartados' : 'Mostrar descartados'}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Box>
          }
        />
      </Box>
      <DownloadLeadsDialog
        open={openDownloadDialog}
        onClose={() => setOpenDownloadDialog(false)}
        businessUnitId={businessUnitId || -1}
        filterModel={JSON.stringify({ items: [...filterModel.items, ...(externalFilterItems ?? [])] })}
        sortModel={JSON.stringify(sortModel)}
        totalLeads={total}
      />
    </Box>
  );
};

export default Leads;
